/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo } from 'react';
import { Alert, Layout, Menu, Button } from 'antd';
import type { MenuProps } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  EnvironmentOutlined,
  ScheduleOutlined,
  BarChartOutlined,
  CameraOutlined,
  ToolOutlined,
  LogoutOutlined,
  CrownOutlined,
  ThunderboltOutlined,
  SaveOutlined,
  AppstoreOutlined,
  ExperimentOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import type { AppDispatch } from '../../store/store';
import { logoutUser } from '../../store/slices/authSlice';
import SaveSelector from './SaveSelector';
import NetworkStatus from './NetworkStatus';
import SimCornerControls from './SimCornerControls';
import { User, canManageUsers as checkCanManageUsers, canManageAttributes, isSuperAdmin, getEventQueuePermissions } from '../../types/permissions';
import { commonStyles } from '../../styles/commonStyles';
import { css } from '@emotion/react';
import { useSaveOptional } from '../../contexts/WorldContext';
import { useRequestHalt } from '../../utils/requestHalt';
import brandMark from '../../assets/home/mark.webp';

const styles = {
  layout: css({ minHeight: '100vh' }),
  logo: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    margin: '14px 12px 18px',
    color: '#DCEBD0',
    fontWeight: 700,
    letterSpacing: '0.06em',
    fontSize: 15,
  }),
  logoMark: css({ width: 32, height: 'auto', display: 'block', flexShrink: 0 }),
  header: css({
    background: '#fff',
    padding: '0 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  }),
  headerLeft: css({ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }),
  headerRight: css({ display: 'flex', alignItems: 'center', gap: 16 }),
  content: css({ margin: '24px 16px 0' }),
  breadcrumbWrapper: css({ margin: '16px 0' }),
};

const { Header, Content, Footer, Sider } = Layout;

interface MainLayoutProps {
  children: React.ReactNode;
  breadcrumb?: React.ReactNode;
}

export default function MainLayout({ children, breadcrumb }: MainLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const authLoading = useSelector((state: any) => state.auth.loading) as boolean;
  const isAuthenticated = useSelector((state: any) => Boolean(state.auth.accessToken));
  const user = useSelector((state: any) => state.auth.user) as User | null;
  const userActions = useSelector((state: any) => state.auth.user?.actions);
  const currentSave = useSaveOptional();
  const requestHalt = useRequestHalt();

  const canManageUsersPermission = useMemo(() => {
    // CN-0008: Новая структура прав
    if (user && typeof userActions === 'object' && 'global' in userActions) {
      return checkCanManageUsers(user);
    }
    // Старая структура (для обратной совместимости)
    return Array.isArray(userActions) && userActions.includes('users.update');
  }, [user, userActions]);

  const canManageCropAttributes = useMemo(() => {
    if (!user) return false;
    if (isSuperAdmin(user)) {
      return true;
    }
    
    // CN-0008: Новая структура прав
    if (typeof userActions === 'object' && 'global' in userActions) {
      return canManageAttributes(user);
    }
    
    // Старая структура (для обратной совместимости)
    if (Array.isArray(userActions)) {
      return (
        userActions.includes('crop_attributes.manage') ||
        userActions.includes('crops.attributes.manage') ||
        userActions.includes('crops.attributes.update')
      );
    }
    return false;
  }, [user, userActions]);

  const saveMatch = useMemo(() => location.pathname.match(/^\/saves\/([^/]+)/), [location.pathname]);
  const currentFarmId = currentSave?.farmId || null;
  const currentSaveId = saveMatch ? saveMatch[1] : null;

  // CN-0008: Проверка прав на просмотр очереди событий
  const eventQueuePermissions = useMemo(() => {
    if (!currentFarmId || !user) return null;
    const perms = getEventQueuePermissions(user, currentFarmId);
    
    // Fallback: если eventQueue не настроен, показываем меню для всех авторизованных пользователей фермы
    if (!perms.canViewMyQueue && !perms.canViewAllQueue && isAuthenticated) {
      return {
        canViewMyQueue: true,
        canViewAllQueue: false,
        canAssignEvents: false,
        canCreateEvents: false,
      };
    }
    
    return perms;
  }, [user, currentFarmId, isAuthenticated]);

  const baseMenuItems = useMemo<MenuProps['items']>(() => [
    {
      key: 'events',
      icon: <ScheduleOutlined />,
      label: 'Мероприятия',
      disabled: true,
    },
    {
      key: 'consumables',
      icon: <ToolOutlined />,
      label: 'Расходники',
      disabled: true,
    },
    {
      key: 'gallery',
      icon: <CameraOutlined />,
      label: 'Фотоархив',
      disabled: true,
    },
    {
      key: 'analytics',
      icon: <BarChartOutlined />,
      label: 'Аналитика',
      disabled: true,
    },
  ], []);

  const farmMenuItems = useMemo<MenuProps['items']>(() => {
    // Save-scoped pages — наполняем меню save-роутами с saveId.
    if (currentSaveId) {
      const saveChildren: MenuProps['items'] = [
        { key: `/saves/${currentSaveId}`, label: 'Мир / grid' },
        { key: `/saves/${currentSaveId}/plantings`, label: 'Посадки' },
      ];
      if (eventQueuePermissions?.canViewMyQueue || eventQueuePermissions?.canViewAllQueue) {
        saveChildren.push({ key: `/saves/${currentSaveId}/tasks`, label: 'Задачи' });
      }
      saveChildren.push(
        { key: `/saves/${currentSaveId}/crops`, label: 'Культуры' },
        { key: `/saves/${currentSaveId}/properties`, label: 'Свойства культур' },
        { key: `/saves/${currentSaveId}/attributes`, label: 'Атрибуты' },
        { key: `/saves/${currentSaveId}/simulation`, icon: <ThunderboltOutlined />, label: 'Симуляция' },
        { key: `/saves/${currentSaveId}/storage`, icon: <SaveOutlined />, label: 'Склад' },
        { key: `/saves/${currentSaveId}/users`, label: 'Доступ' },
      );
      return [
        {
          key: 'save',
          icon: <EnvironmentOutlined />,
          label: 'Сейв',
          children: saveChildren,
        },
      ];
    }

    return [];
  }, [currentSaveId, eventQueuePermissions]);

  const appModeMenuItems = useMemo<MenuProps['items']>(() => {
    const children: MenuProps['items'] = [
      {
        key: '/scenarios',
        icon: <ExperimentOutlined />,
        label: 'Сценарии',
      },
      {
        key: '/saves',
        icon: <PlayCircleOutlined />,
        label: 'Игровые сейвы',
      },
      {
        key: '/plans',
        icon: <SaveOutlined />,
        label: 'Планы сада',
      },
    ];

    children.splice(1, 0, {
      key: '/saves?type=free',
      icon: <ThunderboltOutlined />,
      label: 'Свободная игра',
    });

    return [
      {
        key: 'app-modes',
        icon: <AppstoreOutlined />,
        label: 'Режимы приложения',
        children,
      },
    ];
  }, []);

  const menuItems = useMemo<MenuProps['items']>(() => {
    const items: MenuProps['items'] = [...appModeMenuItems];
    // Сначала добавляем пункты меню фермы, если есть
    if (farmMenuItems.length) {
      items.push(...farmMenuItems);
    }
    // Затем базовые пункты меню
    items.push(...baseMenuItems);

    const adminChildren: MenuProps['items'] = [];
    if (canManageUsersPermission) {
      adminChildren.push({ key: '/admin/users', label: 'Пользователи' });
      adminChildren.push({ key: '/admin/system', label: 'Система (debug)' });
      adminChildren.push({ key: '/admin/seasons', label: 'Турнирные сезоны' });
      adminChildren.push({ key: '/admin/scenarios', label: 'Сценарии — каталог' });
      adminChildren.push({ key: '/admin/scenarios/author', label: 'Сценарии — авторинг' });
      adminChildren.push({ key: '/admin/scenario-debug', label: 'Сценарии — отладка' });
    }
    if (canManageCropAttributes) {
      adminChildren.push({ key: '/admin/attributes', label: 'Каталог атрибутов' });
      adminChildren.push({ key: '/admin/crop-attributes', label: 'Назначение атрибутов' });
      adminChildren.push({ key: '/admin/event-templates', label: 'Шаблоны событий' });
      adminChildren.push({ key: '/admin/cultivation-cards', label: 'Техкарты' });
    }

    if (adminChildren.length === 1) {
      const single = adminChildren[0] as { key: string };
      items.push({ key: single.key, icon: <CrownOutlined />, label: 'Администрирование' });
    } else if (adminChildren.length > 1) {
      items.push({
        key: '/admin',
        icon: <CrownOutlined />,
        label: 'Администрирование',
        children: adminChildren,
      });
    }

    return items;
  }, [appModeMenuItems, baseMenuItems, farmMenuItems, canManageUsersPermission, canManageCropAttributes]);

  const selectedKey = useMemo(() => {
    if (location.pathname.startsWith('/admin/crop-attributes')) return '/admin/crop-attributes';
    if (location.pathname.startsWith('/admin/attributes')) return '/admin/attributes';
    if (location.pathname.startsWith('/admin/event-templates')) return '/admin/event-templates';
    if (location.pathname.startsWith('/admin/cultivation-cards')) return '/admin/cultivation-cards';
    if (location.pathname.startsWith('/admin/system')) return '/admin/system';
    if (location.pathname.startsWith('/admin/seasons')) return '/admin/seasons';
    if (location.pathname.startsWith('/admin/scenario-debug')) return '/admin/scenario-debug';
    if (location.pathname.startsWith('/admin/users')) return '/admin/users';
    if (location.pathname.startsWith('/admin')) return '/admin';
    if (location.pathname.startsWith('/scenarios')) return '/scenarios';
    if (location.pathname.startsWith('/saves')) {
      return currentSave?.type === 'free' ? 'app-free-game' : '/saves';
    }
    return '';
  }, [location.pathname, currentSave?.type]);

  const handleMenuClick: MenuProps['onClick'] = async ({ key }) => {
    if (typeof key === 'string' && key.startsWith('/')) {
      navigate(key);
    }
  };

  const handleLogout = useCallback(async () => {
    try {
      await dispatch(logoutUser()).unwrap();
    } catch (e) {
      // ignore, state already reset on rejection
    } finally {
      navigate('/login');
    }
  }, [dispatch, navigate]);

  return (
    <Layout css={styles.layout}>
      <Sider breakpoint="lg" collapsedWidth="0">
        <div className="logo" css={styles.logo}>
          <img src={brandMark} alt="" css={styles.logoMark} />
          <span>VERDOMATIC</span>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          items={menuItems}
          selectedKeys={[selectedKey]}
          defaultOpenKeys={currentFarmId ? ['app-modes', 'farms', '/admin'] : ['app-modes', ...(canManageUsersPermission && canManageCropAttributes ? ['/admin'] : [])]}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header
          css={styles.header}
        >
          <div css={styles.headerLeft}>
            <SaveSelector />
            <SimCornerControls farmId={currentFarmId || undefined} mode="toolbar" />
          </div>
          <div css={styles.headerRight}>
            <NetworkStatus />
            {isAuthenticated && (
              <Button
                type="text"
                danger
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                loading={authLoading}
              >
                Выйти
              </Button>
            )}
          </div>
        </Header>
        <Content css={styles.content}>
          {breadcrumb && <div css={styles.breadcrumbWrapper}>{breadcrumb}</div>}
          {requestHalt.halted ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Запросы остановлены"
              description={
                requestHalt.status
                  ? `Последний серверный ответ ${requestHalt.status}. Новые запросы не отправляются до перезагрузки страницы.`
                  : requestHalt.message || 'Новые запросы не отправляются до перезагрузки страницы.'
              }
              action={
                <Button size="small" danger onClick={() => window.location.reload()}>
                  Перезагрузить
                </Button>
              }
            />
          ) : null}
          {children}
        </Content>
        <Footer css={commonStyles.textCenter}>Канифоль — Canifole — АгроУчёт ©2025</Footer>
      </Layout>
    </Layout>
  );
}
